export interface FriendlyToolEntry {
    partial: string;
    call: string;
    response: string;
    responseError?: string;
    extractDetail?: (args: any) => string;
}

function extractTableFromSql(query: string): string {
    return extractTableNames(query);
}

function coerceSqlString(args: any): string {
    if (args == null) return '';
    if (typeof args === 'string') return args;
    if (typeof args === 'object') {
        // Try the common shapes for a SQL payload before falling back to
        // a JSON dump (regexes below still match `FROM foo` inside JSON).
        if (typeof args.query === 'string') return args.query;
        if (typeof args.sql === 'string') return args.sql;
        try {
            return JSON.stringify(args);
        } catch {
            return String(args);
        }
    }
    return String(args);
}

function coerceToString(args: any): string {
    if (args == null) return '';
    if (typeof args === 'string') return args;
    try {
        return JSON.stringify(args);
    } catch {
        return String(args);
    }
}

function extractColumnNames(query: string): string {

    if (!query || typeof query !== 'string') return '';
    const matches = [...query.matchAll(/(\w+)\s+AS\s+(\w+)/ig)];
    const columns = matches.map(m => m[2]);
    return columns.length > 0 ? columns.join(', ') : '';

}

function extractTableNames(query: string): string {
    if (!query || typeof query !== 'string') return '';
    const matches = [...query.matchAll(/\bFROM\s+[`"]?([\w.]+)[`"]?/gi)];
    const tables = matches.map(m => m[1]);
    return tables.length > 0 ? tables.join(', ') : '';
}


export const friendlyToolConfigRegistry: Record<string, FriendlyToolEntry> = {
    // ── BigQuery ──
    execute_query: {
        partial: 'Elaborando uma consulta no banco de dados',
        call: 'Executando a consulta no banco de dados',
        response: 'Consulta concluída',
        responseError: 'Ocorreu um erro na consulta, vou corrigir',
        extractDetail: (args) => {
            const query = coerceSqlString(args);
            const columnNames = extractColumnNames(query);
            const table = extractTableFromSql(query);
            return table ? `(Campos:${columnNames}) - (tabela: ${table})` : '';
        },
    },
    execute_parallels_queries: {
        partial: 'Preparando consultas em paralelo',
        call: 'Executando consultas em paralelo',
        response: 'Consultas em paralelo concluídas',
        responseError: 'Erro ao executar consultas em paralelo',
        extractDetail: (args) => {
            const query = coerceSqlString(args);
            const columnNames = extractColumnNames(query);
            const table = extractTableFromSql(query);
            return table ? `(Campos:${columnNames}) - (tabela: ${table})` : '';
        },
    },
    next_page: {
        partial: 'Preparando próxima página de resultados',
        call: 'Obtendo próxima página de resultados',
        response: 'Página de resultados obtida',
    },
    list_tables: {
        partial: 'Preparando listagem de tabelas',
        call: 'Listando tabelas disponíveis',
        response: 'Tabelas listadas',
    },
    get_table_schema: {
        partial: 'Preparando consulta de schema',
        call: 'Obtendo schema da tabela',
        response: 'Schema obtido',
        extractDetail: (args) => coerceToString(args),
    },
    get_multiple_table_schemas: {
        partial: 'Preparando consulta de schemas',
        call: 'Obtendo schemas das tabelas',
        response: 'Schemas obtidos',
        extractDetail: (args) => coerceToString(args),
    },

    // ── CommandLine ──
    command_line: {
        partial: 'Preparando comando',
        call: 'Executando comando',
        response: 'Comando executado',
        responseError: 'Erro ao executar o comando',
    },

    // ── DashboardAnalysis ──
    get_all_dashboard_dards_data: {
        partial: 'Preparando análise do dashboard',
        call: 'Obtendo dados dos cards do dashboard',
        response: 'Dados dos cards obtidos',
        extractDetail: (args) => args?.dashboardId ? `(dashboard: ${args.dashboardId})` : '',
    },

    // ── DashboardAnalysis ──
    "imagedescription-analyze_image": {
        partial: 'Preparando análise da imagem',
        call: 'Gerando análise da imagem',
        response: 'Dados dos da imagem obtidos',
        extractDetail: (args) => args?.mime_type ? `(dashboard: ${args.mime_type})` : '',
    },

    "imagedescription-analyze image": {
        partial: 'Preparando análise da imagem...',
        call: 'Gerando análise da imagem',
        response: 'Dados dos da imagem obtidos',
        extractDetail: (args) => args?.mime_type ? `(dashboard: ${args.mime_type})` : '',
    },
    // ── DashboardComposer ──
    patch_card_file: {
        partial: 'Preparando alteração no card',
        call: 'Atualizando arquivo do card',
        response: 'Arquivo do card atualizado',
        extractDetail: (args) => args?.cardFileName ? `(${args.cardFileName})` : '',
    },
    create_update_card: {
        partial: 'Preparando criação/atualização do card',
        call: 'Criando ou atualizando card',
        response: 'Card criado/atualizado',
        extractDetail: (args) => args?.cardKey ? `(card: ${args.cardKey})` : '',
    },
    build_dashboard: {
        partial: 'Preparando build do dashboard',
        call: 'Construindo dashboard',
        response: 'Dashboard construído',
    },
    rebuild_dashboard: {
        partial: 'Preparando rebuild do dashboard',
        call: 'Reconstruindo dashboard',
        response: 'Dashboard reconstruído',
    },

    // ── DashboardTool ──
    list_session_dashboards: {
        partial: 'Preparando listagem de dashboards da sessão',
        call: 'Listando dashboards da sessão',
        response: 'Dashboards da sessão listados',
    },
    save_dashboard: {
        partial: 'Preparando salvamento do dashboard',
        call: 'Salvando dashboard',
        response: 'Dashboard salvo',
        extractDetail: (args) => args?.description ? `(${args.description})` : '',
    },
    list_dashboard: {
        partial: 'Preparando listagem de dashboards',
        call: 'Listando dashboards',
        response: 'Dashboards listados',
    },

    // ── DataSourceTool ──
    remove_data_source: {
        partial: 'Preparando remoção de data source',
        call: 'Removendo data source',
        response: 'Data source removido',
    },
    list_views_and_sources: {
        partial: 'Preparando listagem de views e data sources',
        call: 'Listando views e data sources',
        response: 'Views e data sources listados',
    },
    create_data_source: {
        partial: 'Preparando criação de data source',
        call: 'Criando data source',
        response: 'Data source criado',
        extractDetail: (args) => args?.name ? `(${args.name})` : '',
    },
    create_or_update_card_view: {
        partial: 'Preparando view do card',
        call: 'Criando/atualizando view do card',
        response: 'View do card atualizada',
        extractDetail: (args) => args?.viewName ? `(${args.viewName})` : '',
    },
    create_or_update_python_view: {
        partial: 'Preparando view Python',
        call: 'Criando/atualizando view Python',
        response: 'View Python atualizada',
        extractDetail: (args) => args?.name ? `(${args.name})` : '',
    },
    update_data_source: {
        partial: 'Preparando atualização de data source',
        call: 'Atualizando data source',
        response: 'Data source atualizado',
    },

    // ── Files ──
    create_file: {
        partial: 'Preparando criação de arquivo',
        call: 'Criando arquivo',
        response: 'Arquivo criado',
        extractDetail: (args) => args?.fileName ? `(${args.fileName})` : '',
    },
    read_file: {
        partial: 'Preparando leitura de arquivo',
        call: 'Lendo arquivo',
        response: 'Arquivo lido',
        extractDetail: (args) => args?.fileName ? `(${args.fileName})` : '',
    },
    read_file_tail: {
        partial: 'Preparando leitura do final do arquivo',
        call: 'Lendo final do arquivo',
        response: 'Final do arquivo lido',
        extractDetail: (args) => args?.fileName ? `(${args.fileName})` : '',
    },
    patch_file: {
        partial: 'Preparando alteração no arquivo',
        call: 'Alterando arquivo',
        response: 'Arquivo alterado',
        extractDetail: (args) => args?.fileName ? `(${args.fileName})` : '',
    },
    overwrite_file: {
        partial: 'Preparando substituição de arquivo',
        call: 'Substituindo conteúdo do arquivo',
        response: 'Arquivo substituído',
        extractDetail: (args) => args?.fileName ? `(${args.fileName})` : '',
    },
    list_files: {
        partial: 'Preparando listagem de arquivos',
        call: 'Listando arquivos',
        response: 'Arquivos listados',
    },
    delete_file: {
        partial: 'Preparando exclusão de arquivo',
        call: 'Excluindo arquivo',
        response: 'Arquivo excluído',
        extractDetail: (args) => args?.fileName ? `(${args.fileName})` : '',
    },
    file_exists: {
        partial: 'Verificando existência de arquivo',
        call: 'Verificando arquivo',
        response: 'Verificação concluída',
        extractDetail: (args) => args?.fileName ? `(${args.fileName})` : '',
    },

    // ── GoogleSearch ──
    google_search: {
        partial: 'Preparando pesquisa na internet',
        call: 'Pesquisando na internet',
        response: 'Pesquisa concluída',
        extractDetail: (args) => args?.query ? `("${args.query}")` : '',
    },

    // ── LazyInstructionLoader ──
    get_serviceitem_instructions: {
        partial: 'Preparando carregamento de instruções',
        call: 'Carregando instruções do serviço',
        response: 'Instruções carregadas',
    },
    get_css_stylesheet: {
        partial: 'Preparando carregamento de estilos CSS',
        call: 'Carregando estilos CSS',
        response: 'Estilos CSS carregados',
    },

    // ── MemoryLibrary ──
    search_memory_suggestions: {
        partial: 'Preparando busca de sugestões na memória',
        call: 'Buscando sugestões na memória',
        response: 'Sugestões encontradas',
    },
    search_memories: {
        partial: 'Preparando busca na memória',
        call: 'Buscando na memória',
        response: 'Busca na memória concluída',
        extractDetail: (args) => args?.query ? `("${args.query}")` : '',
    },
    create_memory: {
        partial: 'Preparando criação de memória',
        call: 'Criando memória',
        response: 'Memória criada',
    },
    update_memory: {
        partial: 'Preparando atualização de memória',
        call: 'Atualizando memória',
        response: 'Memória atualizada',
    },
    delete_memory: {
        partial: 'Preparando exclusão de memória',
        call: 'Excluindo memória',
        response: 'Memória excluída',
    },
    count_memories: {
        partial: 'Preparando contagem de memórias',
        call: 'Contando memórias',
        response: 'Contagem concluída',
    },

    // ── Network ──
    curl: {
        partial: 'Preparando requisição HTTP',
        call: 'Executando requisição HTTP',
        response: 'Requisição concluída',
        extractDetail: (args) => args?.url ? `(${args.url})` : '',
    },
    markdown_curl: {
        partial: 'Preparando requisição HTTP (Markdown)',
        call: 'Obtendo conteúdo da página',
        response: 'Conteúdo obtido',
        extractDetail: (args) => args?.url ? `(${args.url})` : '',
    },

    // ── Python ──
    run_python: {
        partial: 'Preparando código Python',
        call: 'Executando código Python',
        response: 'Código Python executado',
        responseError: 'Erro na execução do Python, vou corrigir',
        extractDetail: (args) => args?.fileName ? `(${args.fileName})` : '',
    },

    // ── ScriptManager ──
    script_create_or_update: {
        partial: 'Preparando script de análise',
        call: 'Criando/atualizando script',
        response: 'Script salvo',
        extractDetail: (args) => args?.name ? `(${args.name})` : '',
    },
    script_code_execute: {
        partial: 'Preparando execução do script',
        call: 'Executando script',
        response: 'Script executado',
        responseError: 'Erro na execução do script',
    },
    script_get_metadata: {
        partial: 'Preparando consulta de metadados do script',
        call: 'Obtendo metadados do script',
        response: 'Metadados obtidos',
    },
    script_get: {
        partial: 'Preparando leitura do script',
        call: 'Obtendo script',
        response: 'Script obtido',
    },
    script_list: {
        partial: 'Preparando listagem de scripts',
        call: 'Listando scripts',
        response: 'Scripts listados',
    },
    script_code_update: {
        partial: 'Preparando atualização do código',
        call: 'Atualizando código do script',
        response: 'Código atualizado',
    },
    script_code_commit: {
        partial: 'Preparando commit do script',
        call: 'Fazendo commit do script',
        response: 'Commit realizado',
    },
    script_code_rollback: {
        partial: 'Preparando rollback do script',
        call: 'Revertendo alterações do script',
        response: 'Rollback realizado',
    },
    script_remove: {
        partial: 'Preparando remoção do script',
        call: 'Removendo script',
        response: 'Script removido',
    },

    // ── SubAgent ──
    start_sub_agent: {
        partial: 'Preparando sub-agente',
        call: 'Iniciando sub-agente',
        response: 'Sub-agente iniciado',
        extractDetail: (args) => args?.displayName ? `(${args.displayName})` : '',
    },
    get_sub_agent_status: {
        partial: 'Verificando status do sub-agente',
        call: 'Consultando status do sub-agente',
        response: 'Status obtido',
    },
    list_sub_agents: {
        partial: 'Preparando listagem de sub-agentes',
        call: 'Listando sub-agentes',
        response: 'Sub-agentes listados',
    },
    wait_on_subagent: {
        partial: 'Aguardando sub-agente',
        call: 'Aguardando conclusão do sub-agente',
        response: 'Terminei de aguardar, vou verificar status',
    },
    cancel_sub_agent: {
        partial: 'Preparando cancelamento do sub-agente',
        call: 'Cancelando sub-agente',
        response: 'Sub-agente cancelado',
    },
    send_message_to_sub_agent: {
        partial: 'Preparando mensagem para sub-agente',
        call: 'Enviando mensagem ao sub-agente',
        response: 'Mensagem enviada ao sub-agente',
    },

    // ── TodoList ──
    create_todo_list: {
        partial: 'Criando lista de tarefas',
        call: 'Enviando lista de tarefas',
        response: 'Lista de tarefas criada',
    },
    update_todo_list: {
        partial: 'Atualizando lista de tarefas',
        call: 'Salvando atualização da lista',
        response: 'Lista de tarefas atualizada',
    },
};

export function configureFriendlyMapping(toolName:string, mapping: FriendlyToolEntry) {
    friendlyToolConfigRegistry[toolName] = mapping;
}

export type FriendlyState = 'partial' | 'call' | 'response';

export function getFriendlyLabel(toolName: string, state: FriendlyState, args?: any, isError?: boolean): string {
    const parts = toolName.split('-');

    const pureToolName = parts.length > 1 ? parts.slice(1).join('') : toolName;
    const entry = friendlyToolConfigRegistry[pureToolName];
    if (!entry) {
        const pretty = toolName.replace(/_/g, ' ');
        switch (state) {
            case 'partial': return `Preparando ${pretty}`;
            case 'call': return `Executando ${pretty}`;
            case 'response': return `${pretty} concluído`;
        }
    }

    if (state === 'response' && isError && entry.responseError) {
        return entry.responseError;
    }

    let label = entry[state];
    if (entry.extractDetail) {
        const detail = entry.extractDetail(args);
        if (detail) label = `${label} ${detail}`;
    }
    return label;
}
